// @flow
import React, { memo } from 'react';
import classnames from 'classnames';

import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';

import { Badge, type BadgeProps } from 'Components/frameworks/badge/Badge.jsx';
import DynamicLink, {
  type Props as DynamicLinkProps,
} from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import { Title } from 'Components/frameworks/title/Title.jsx';

export type ProductHeaderProps = {
  badge?: ?string,
  badgeColor?: string,
  badgeIcon?: BadgeProps,
  className?: string,
  isLinkDisabled?: boolean,
  isExclusiveCollection?: boolean,
  heading: { title: string, subtitle?: string },
  link?: DynamicLinkProps,
  hasRelativeElement?: boolean,
};

export const ProductHeader = memo<ProductHeaderProps>(function ProductHeader(
  props: ProductHeaderProps,
) {
  const {
    badge,
    badgeColor = COLORS.BLUE,
    badgeIcon = {},
    heading = {},
    isExclusiveCollection,
    isLinkDisabled,
    link,
    className,
    hasRelativeElement = false,
  } = props;
  const icon = badgeIcon.iconName ? badgeIcon.iconName : '';
  const iconName = icon === ICONS.TRIDENT45 ? ICONS.TRIDENT4 : icon;

  const { title = '', subtitle = '' } = heading;

  const TagName = isLinkDisabled ? 'div' : DynamicLink;

  return (
    <header
      className={classnames('flex', className, {
        relative: !hasRelativeElement,
      })}
    >
      <TagName
        className="reset-anchor flex w-full z-1 min-w-0"
        data-testid="ProductHeader-link"
        {...(isLinkDisabled ? null : link)}
      >
        {!isLinkDisabled && (
          <div className="absolute inset-0" data-testid="ProductHeader-link-overlay" />
        )}
        <div className="relative flex-1 flex flex-col-reverse justify-center -mb-px overflow-hidden rtl:pr-1">
          <Title
            className="flex items-center font-brand leading-one text-lg"
            color={COLORS.GRAY_DARKER}
            hasEllipsis
            hLevel={2}
            role="presentation"
            theme="none"
          >
            {title}
          </Title>
          <div className="flex">
            <Title
              className="flex min-w-0 items-center font-bold font-sans text-sm uppercase tracking-tight"
              color={COLORS.GRAY_MEDIUM}
              hasEllipsis
              hLevel={3}
              role="presentation"
              theme="none"
            >
              {subtitle}
            </Title>
            {!!badge && (
              <div
                data-testid="ProductHeader-seasonBadge"
                className={classnames(
                  `bg-${badgeColor}`,
                  'leading-one p-1 font-bold uppercase rounded-tiny text-center text-white text-xs whitespace-no-wrap rtl:ml-2',
                )}
              >
                <div className="-mb-px">{badge}</div>
              </div>
            )}
          </div>
        </div>
      </TagName>
      {!isExclusiveCollection ||
      (icon === ICONS.TRIDENT45 || icon === ICONS.TRIDENT6 || icon === ICONS.CRUISE) ? (
        <Badge
          {...badgeIcon}
          iconName={iconName}
          iconSize=""
          className="-mb-px rounded-tiny"
          size={32}
        />
      ) : null}
      {(isExclusiveCollection || icon === ICONS.TRIDENT45) && icon !== ICONS.CRUISE ? (
        <div
          style={{ height: '32px' }}
          className="bg-deepBlue px-2 flex items-center rtl:ml-1 inset-border-after--small"
        >
          <Icon
            name={ICONS.EXCLUSIVE_COLLECTION}
            width="calc(45rem / 16)"
            color={COLORS.WHITE}
            isNotSquare
          />
        </div>
      ) : null}
    </header>
  );
});
